import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTiers(data.tiers || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setLoading(false);
    }
  };

  const handleEdit = (tier) => {
    setEditingTier(tier.id);
    setFormData({
      price_monthly: tier.price_monthly,
      price_annual: tier.price_annual,
      promotion_price_monthly: tier.promotion_price_monthly || '',
      promotion_price_annual: tier.promotion_price_annual || '',
      promotion_start_date: tier.promotion_start_date ? tier.promotion_start_date.slice(0, 16) : '',
      promotion_end_date: tier.promotion_end_date ? tier.promotion_end_date.slice(0, 16) : '',
      is_active: tier.is_active
    });
  };

  const handleSave = async (tierId) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/${tierId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          promotion_price_monthly: formData.promotion_price_monthly || null,
          promotion_price_annual: formData.promotion_price_annual || null,
          promotion_start_date: formData.promotion_start_date || null,
          promotion_end_date: formData.promotion_end_date || null
        })
      });

      if (response.ok) {
        alert('✅ Pricing updated successfully!');
        setEditingTier(null);
        fetchPricing();
      } else {
        alert('Failed to update pricing');
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
      alert('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleClearPromotion = async (tierId) => {
    if (!window.confirm('Clear all promotional pricing for this tier?')) {
      return;
    }

    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/${tierId}/clear-promotion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('✅ Promotion cleared!');
        fetchPricing();
      } else {
        alert('Failed to clear promotion');
      }
    } catch (error) {
      console.error('Error clearing promotion:', error);
      alert('Failed to clear promotion');
    }
  };

  const calculateAnnualSavings = (monthly, annual) => {
    if (!monthly || !annual) return 0;
    const monthlyCost = parseFloat(monthly) * 12;
    const annualCost = parseFloat(annual);
    const savings = monthlyCost - annualCost;
    const percent = (savings / monthlyCost) * 100;
    return Math.round(percent);
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Pricing Management</h1>
        <p className="text-gray-600">Manage subscription pricing, annual discounts, and promotional sales</p>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading pricing...</div>
      ) : (
        <div className="space-y-6">
          {tiers.map((tier) => (
            <div key={tier.id} className="bg-white rounded-xl shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">{tier.name}</h2>
                  <p className="text-gray-600">
                    {tier.games_per_month === -1 ? 'Unlimited' : tier.games_per_month} games per month
                  </p>
                  {tier.promotion_active && (
                    <span className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold mt-2">
                      🔥 Sale Active
                    </span>
                  )}
                  {!tier.is_active && (
                    <span className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold mt-2">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {editingTier === tier.id ? (
                    <>
                      <button
                        onClick={() => handleSave(tier.id)}
                        disabled={saving}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingTier(null)}
                        className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg font-semibold transition"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEdit(tier)}
                        className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded-lg font-semibold transition"
                      >
                        Edit Pricing
                      </button>
                      {(tier.promotion_price_monthly || tier.promotion_price_annual) && (
                        <button
                          onClick={() => handleClearPromotion(tier.id)}
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition"
                        >
                          Clear Sale
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {editingTier === tier.id ? (
                /* Edit Mode */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Regular Pricing */}
                  <div>
                    <h3 className="font-bold text-gray-800 mb-3">Regular Pricing</h3>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Monthly Price ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_monthly}
                        onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Annual Price ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_annual}
                        onChange={(e) => setFormData({ ...formData, price_annual: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {calculateAnnualSavings(formData.price_monthly, formData.price_annual)}% savings vs monthly
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="mr-2 h-4 w-4"
                        />
                        <span className="text-sm font-semibold text-gray-700">Active (visible to hosts)</span>
                      </label>
                    </div>
                  </div>

                  {/* Promotional Pricing */}
                  <div>
                    <h3 className="font-bold text-gray-800 mb-3">Sale Pricing (Optional)</h3>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Sale Monthly Price ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.promotion_price_monthly}
                        onChange={(e) => setFormData({ ...formData, promotion_price_monthly: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                        placeholder="Leave empty for no sale"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Sale Annual Price ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.promotion_price_annual}
                        onChange={(e) => setFormData({ ...formData, promotion_price_annual: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                        placeholder="Leave empty for no sale"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Sale Start Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.promotion_start_date}
                        onChange={(e) => setFormData({ ...formData, promotion_start_date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Sale End Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.promotion_end_date}
                        onChange={(e) => setFormData({ ...formData, promotion_end_date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Regular Pricing Display */}
                  <div>
                    <h3 className="font-bold text-gray-800 mb-3">Regular Pricing</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly:</span>
                        <span className="font-semibold text-lg">${tier.price_monthly}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Annual:</span>
                        <span className="font-semibold text-lg">
                          ${tier.price_annual}
                          <span className="text-sm text-green-600 ml-2">
                            ({calculateAnnualSavings(tier.price_monthly, tier.price_annual)}% savings)
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Promotional Pricing Display */}
                  <div>
                    <h3 className="font-bold text-gray-800 mb-3">Sale Pricing</h3>
                    {tier.promotion_price_monthly || tier.promotion_price_annual ? (
                      <div className="space-y-2">
                        {tier.promotion_price_monthly && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sale Monthly:</span>
                            <span className="font-semibold text-lg text-red-600">${tier.promotion_price_monthly}</span>
                          </div>
                        )}
                        {tier.promotion_price_annual && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sale Annual:</span>
                            <span className="font-semibold text-lg text-red-600">${tier.promotion_price_annual}</span>
                          </div>
                        )}
                        {tier.promotion_start_date && tier.promotion_end_date && (
                          <div className="mt-3 text-sm text-gray-600">
                            <div>Starts: {new Date(tier.promotion_start_date).toLocaleString()}</div>
                            <div>Ends: {new Date(tier.promotion_end_date).toLocaleString()}</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic">No active sale</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
